import type { Session } from '@colplat/core-client';
import type { Uuid } from '@colplat/protocol';

/**
 * A lean table shell: the gutter (presence-colored) and structure only. The
 * caller renders each row's own cells (title button, status badges, etc.) —
 * this keeps the component reusable without guessing at a schema-driven
 * generic column system nothing here needs yet.
 *
 * Deliberately does not implement the reference demo's "defer new arrivals
 * while scrolled/typing" policy — administration's user list is low-volume
 * enough that live reordering isn't a practical problem worth the added
 * complexity yet.
 */
export interface RecordTableRow {
  id: Uuid;
  /** Inner `<td>`s for this row, excluding the gutter cell. */
  html: string;
  /**
   * Extra attributes for the `<tr>` itself, already escaped and
   * space-prefixed — for the data a row-level gesture needs to read, such as
   * the sibling and type a drag-to-reorder is allowed within.
   */
  attrs?: string;
}

export interface RecordTableOptions {
  session: Session;
  entity: string;
  headers: string[];
  rows: RecordTableRow[];
}

export function renderRecordTable(container: HTMLElement, opts: RecordTableOptions): void {
  const headerHtml = opts.headers.map((h) => `<th>${h}</th>`).join('');
  const rowsHtml = opts.rows.map((row) => {
    const present = opts.session.presence(opts.entity, row.id);
    const holder = present[0];
    const cls = present.length > 0 ? 'is-open' : '';
    return (
      `<tr class="${cls}" data-row="${row.id}" style="--row-color:${holder ? holder.colorHex : 'transparent'}"${row.attrs ?? ''}>` +
      `<td class="cf-gut"><span></span></td>${row.html}</tr>`
    );
  }).join('');

  // Wide content (many badges, a long name) must scroll inside its own box,
  // never bleed past the card into the page — the table itself has no width
  // limit, so without this wrapper it just overflows its container silently.
  container.innerHTML =
    `<div class="cf-table-scroll"><table class="table cf-table align-middle">` +
    `<thead><tr><th class="cf-gut"></th>${headerHtml}</tr></thead>` +
    `<tbody>${rowsHtml}</tbody></table></div>`;
}
